import axios, { Axios } from 'axios';
import { ContentItemInternal, SparseCredential, InternalStoreCredential } from './types';
import { VERSION } from './version';
import crypto from 'crypto';

export const DEFAULT_IDENTITY_STORE_ENDPOINT = 'https://api.civic.com/identity-store/v2';

class IdentityStoreClient {
    private axiosInstance: Axios;
    private _evidenceAxiosInstance: Axios;
    constructor(readonly did: string, readonly auth: string, readonly endpoint: string = DEFAULT_IDENTITY_STORE_ENDPOINT ) {
        this.axiosInstance = axios.create({
            baseURL: `${this.endpoint}/${this.did}`,
            headers: {
                'x-civic-client-version': VERSION,
                Authorization: `Bearer ${this.auth}`,
            },
            //withCredentials: true
          });
    }

    protected get evidenceAxiosInstance() {
        this._evidenceAxiosInstance = axios.create({});
        return this._evidenceAxiosInstance;
    }

    async getCredential(id: string): Promise<InternalStoreCredential | null> {
        const response = await this.axiosInstance.get<InternalStoreCredential>(`/credential/${id}`);
        return response.data || null;
    }

    async findCredentials(tags?: string[]): Promise<SparseCredential[]> {
        const response = await this.axiosInstance.get<SparseCredential[]>('/credential', {
            params: {
                tags,
            },
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status === 404; // allow the 'not found' status code
            },
        });
        return response.data;
    }

    async storeCredential(credential: InternalStoreCredential): Promise<InternalStoreCredential> {
        return (await this.axiosInstance.post<InternalStoreCredential>('/credential', credential)).data;
    }

    async patchCredential(credential: InternalStoreCredential) {
        await Promise.all(credential.content.map(contentItem =>
            this.axiosInstance.patch(`/credential/${credential.credentialId}/content`, contentItem)
        ));
    }

    async getDataFromStorage(contentItem: ContentItemInternal): Promise<string> {
        if(!contentItem.presignedUrl) {
            throw new Error('Trying to retrieve from undefined storage location!');
        }

        // we need to use a different axios instance than the store one, as the auth mechanism is different
        const response = await this.evidenceAxiosInstance.get(contentItem.presignedUrl, { responseType: 'arraybuffer' });

        const base64String = Buffer.from(response.data, 'binary').toString('base64');
        return base64String; 
    }

    async storeDataToStorage(credentialId: string, contentItem: ContentItemInternal, blob: string) {
        if(!contentItem.presignedUrl) {
            throw new Error('Trying to store to undefined storage location!');
        }

        // Decode the base64 encryptedBlob to binary
        const binaryBlob = Buffer.from(blob, 'base64');

        // Use a different axios instance than the store one, as the auth mechanism is different
        await this.evidenceAxiosInstance.put(contentItem.presignedUrl, binaryBlob, { headers: { 'Content-Type': 'application/octet-stream' } });

        // Hash the encrypted binary blob
        const hash = crypto.createHash('sha256').update(binaryBlob).digest('hex');
        contentItem.ref = hash;

        await this.axiosInstance.patch(`/credential/${credentialId}/content`, {
            name: contentItem.name,
            ref: contentItem.ref,
        });
    }

    async deleteCredential(id: string): Promise<void> {
        await this.axiosInstance.delete(`/credential/${id}`);
    }
}

export default IdentityStoreClient;
